/**
 * B2BPdfWorkflow — Phase 2A.1 + 2C.1 + 2C.2 (split workflow PDF)
 *
 * Composant admin à intégrer dans OrderDetail. Une fois la cotation PDF
 * téléversée, deux workflows distincts sont proposés à l'utilisateur :
 *
 *   Option 1 · Envoi simple WhatsApp
 *     - Génère un message pré-rempli (clinique + référence + lien PDF brut)
 *     - Bouton "Envoyer à la clinique" : ouvre wa.me avec numéro de la fiche
 *     - Bouton "Copier le message"   : copie texte + ouvre WhatsApp sans n°
 *     - Chaque envoi est tracé côté backend (b2b_cotation_sends + audit log)
 *     - PAS d'extraction IA · PAS de signature électronique
 *
 *   Option 2 · Extraction + signature électronique
 *     - Parse le PDF Médiciel via pdfplumber
 *     - Permet de valider/éditer les lignes
 *     - Génère un lien public pour signature côté clinique
 *
 * Pré-requis : ordre déjà créé + PDF cotation uploadé (CotationUploadCard).
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkles, Send, CheckCircle2, Loader2, X, MessageCircle, Copy, Trash2, Plus,
  PenLine, Share2, Phone,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('dermascan_token')}` });

const fmtFCFA = (n) => (n == null ? '—' : `${Math.round(n).toLocaleString('fr-FR')} FCFA`);

// Référence courte pour le message WhatsApp (8 premiers chars de l'UUID en MAJ)
const shortRef = (order) => (order?.id ? String(order.id).slice(0, 8).toUpperCase() : '—');

// URL de téléchargement public du PDF brut (token côté backend, sans auth)
const rawPdfUrl = (order) => {
  if (!order?.cotation_download_token) return null;
  const base = process.env.REACT_APP_BACKEND_URL.replace(/\/$/, '');
  return `${base}/api/b2b/cotations/${order.id}/${order.cotation_download_token}`;
};

// Message WhatsApp pré-rempli (texte demandé par l'utilisateur)
const buildWhatsAppMessage = (order, pdfUrl) => {
  const clinic = order?.clinic_name || 'cher partenaire';
  const ref = shortRef(order);
  return (
    `Bonjour ${clinic},\n\n` +
    `Veuillez trouver ci-dessous la cotation de la Pharmacie Saint Ange Des Amis ` +
    `relative à votre demande B2B n° ${ref} :\n${pdfUrl}\n\n` +
    `Merci de nous confirmer votre accord ou de revenir vers nous en cas de précision nécessaire.\n\n` +
    `Cordialement,\nPharmacie Saint Ange Des Amis`
  );
};

// ─── Modale "Extraire & valider les lignes" ──────────────────────────────

const ExtractModal = ({ orderId, onClose, onApplied }) => {
  const [phase, setPhase] = useState('parsing'); // parsing | preview | applying
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);

  React.useEffect(() => {
    (async () => {
      try {
        const r = await axios.post(`${API}/b2b/orders/${orderId}/parse-cotation-pdf`, {}, { headers: auth() });
        setItems(r.data.items || []);
        setMeta(r.data.meta || null);
        setPhase('preview');
      } catch (e) {
        const d = e.response?.data?.detail;
        setError(typeof d === 'string' ? d : 'Erreur lors du parsing du PDF');
        setPhase('error');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (i, k, v) => setItems((a) => a.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const del = (i) => setItems((a) => a.filter((_, idx) => idx !== i));
  const addLine = () => setItems((a) => [...a, { code: '', designation: '', quantity: 1, unit_price: 0, total_ttc: 0, source: 'manual' }]);
  const total = items.reduce((s, x) => s + (x.total_ttc != null ? x.total_ttc : (x.quantity || 0) * (x.unit_price || 0)), 0);

  const apply = async () => {
    const valid = items.filter((x) => x.designation?.trim());
    if (!valid.length) { toast.error('Au moins une ligne avec désignation est requise.'); return; }
    setPhase('applying');
    try {
      const r = await axios.post(`${API}/b2b/orders/${orderId}/items/apply`, {
        items: valid,
        total_amount: total,
      }, { headers: auth() });
      toast.success(`Dossier synchronisé · ${r.data.nb_items} ligne(s), ${fmtFCFA(r.data.total_amount)}`);
      onApplied?.();
      onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : "Erreur lors de l'application");
      setPhase('preview');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-3" onClick={onClose} data-testid="extract-modal">
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[calc(100dvh-80px)] flex flex-col overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-violet-600 to-violet-700 px-5 py-3 text-white flex items-center justify-between">
          <h2 className="font-semibold flex items-center gap-2"><Sparkles className="w-5 h-5" /> Extraction PDF Médiciel</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/10"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 overflow-y-auto">
          {phase === 'parsing' && (
            <div className="text-center py-10">
              <Loader2 className="w-10 h-10 animate-spin mx-auto text-violet-600 mb-3" />
              <p className="text-sm text-zinc-600">Analyse du PDF en cours…</p>
            </div>
          )}

          {phase === 'error' && (
            <div className="text-center py-8">
              <p className="text-red-700 font-medium mb-2">Impossible de parser ce PDF</p>
              <p className="text-sm text-zinc-600 max-w-md mx-auto">{error}</p>
              <p className="text-xs text-zinc-500 mt-4 italic">
                Vérifiez qu'il s'agit bien d'un PDF Médiciel exporté directement (avec colonnes Identifiant / Produit / …), non d'un scan.
              </p>
            </div>
          )}

          {(phase === 'preview' || phase === 'applying') && (
            <div className="space-y-3">
              <div className="bg-violet-50 rounded-lg p-3 text-sm">
                <p className="font-semibold text-violet-900">
                  ✨ {items.length} ligne(s) extraite(s) {meta?.doc_type === 'proforma' ? '(proforma)' : ''}
                </p>
                {meta?.total_ttc != null && (
                  <p className="text-xs text-violet-700 mt-1">
                    Total détecté dans le PDF : <strong>{fmtFCFA(meta.total_ttc)}</strong>
                    {meta.total_ht != null && ` · HT ${fmtFCFA(meta.total_ht)}`}
                    {meta.remise_total != null && meta.remise_total > 0 && ` · Remise ${fmtFCFA(meta.remise_total)}`}
                  </p>
                )}
                <p className="text-xs text-zinc-500 mt-1">
                  Vérifiez les lignes ci-dessous. Vous pouvez modifier, ajouter ou supprimer avant de valider.
                </p>
              </div>

              <div className="space-y-1.5">
                {items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-1.5 items-center p-1.5 bg-zinc-50 rounded text-xs">
                    <Input className="col-span-2 text-xs h-8" placeholder="Code" value={it.code || ''} onChange={(e) => update(i, 'code', e.target.value)} />
                    <Input className="col-span-5 text-xs h-8" placeholder="Désignation *" value={it.designation || ''} onChange={(e) => update(i, 'designation', e.target.value)} />
                    <Input className="col-span-1 text-xs h-8" type="number" min="0" step="0.5" placeholder="Qté" value={it.quantity} onChange={(e) => update(i, 'quantity', parseFloat(e.target.value) || 0)} />
                    <Input className="col-span-2 text-xs h-8" type="number" min="0" placeholder="PU TTC" value={it.unit_price} onChange={(e) => update(i, 'unit_price', parseFloat(e.target.value) || 0)} />
                    <span className="col-span-1 text-right text-xs font-semibold text-emerald-700">
                      {Math.round((it.total_ttc != null ? it.total_ttc : (it.quantity || 0) * (it.unit_price || 0))).toLocaleString('fr-FR')}
                    </span>
                    <Button variant="ghost" size="icon" className="col-span-1 h-7 w-7" onClick={() => del(i)}>
                      <Trash2 className="w-3 h-3 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>

              <div className="flex justify-between items-center pt-2 border-t border-zinc-200">
                <Button type="button" variant="outline" size="sm" onClick={addLine}>
                  <Plus className="w-3 h-3 mr-1" /> Ligne
                </Button>
                <div className="text-sm">
                  Total calculé : <span className="font-bold text-emerald-700">{fmtFCFA(total)}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {phase === 'preview' && (
          <div className="shrink-0 border-t border-zinc-200 bg-white px-4 py-3 flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Annuler</Button>
            <Button onClick={apply} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="extract-apply-btn">
              <CheckCircle2 className="w-4 h-4 mr-1" /> Appliquer au dossier ({items.length})
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Section principale ──────────────────────────────────────────────────

export const B2BPdfWorkflowSection = ({ order, canWrite, onChanged }) => {
  const [extractOpen, setExtractOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [simpleSendBusy, setSimpleSendBusy] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const hasPdf = !!order.cotation_storage_path || !!order.cotation_name;
  const hasValidationToken = !!order.cotation_validation_token;
  const validationStatus = order.cotation_validation_status;
  const isValidated = validationStatus === 'validated';
  const isModRequested = validationStatus === 'modification_requested';
  const isRejected = validationStatus === 'rejected';
  const modRequest = order.cotation_modification_request;
  const rejection = order.cotation_rejection;
  const pdfUrl = rawPdfUrl(order);
  const clinicPhone = order.phone || ''; // numéro tel saisi sur le dossier (souvent celui de la clinique)
  const previewMsg = pdfUrl ? buildWhatsAppMessage(order, pdfUrl) : '';

  const publicUrl = order.cotation_validation_token
    ? `${window.location.origin}/cotation/${order.cotation_validation_token}`
    : null;

  // ─── Option 1 · Envoi simple WhatsApp (raw PDF) ────────────────────────
  // Appelle l'endpoint backend qui historise l'envoi (b2b_cotation_sends +
  // audit log + auto-transition statut → cotation_envoyee). On utilise la
  // wa.me URL retournée pour ouvrir WhatsApp.
  const sendRawPdfWhatsApp = async ({ withPhone }) => {
    if (!pdfUrl) { toast.error('PDF non disponible. Re-téléversez la cotation.'); return; }
    if (withPhone && !clinicPhone) {
      toast.error('Aucun numéro sur le dossier. Utilisez « Copier & ouvrir WhatsApp » à la place.');
      return;
    }
    setSimpleSendBusy(true);
    try {
      const payload = {
        channel: 'whatsapp',
        message: previewMsg,
      };
      if (withPhone) {
        payload.phone = clinicPhone;
      }
      // En mode "sans numéro", on omet `phone` : le backend retombera sur le
      // numéro du dossier pour la traçabilité, sans générer de wa.me que l'on
      // utiliserait — on ouvre nous-même wa.me sans numéro.
      const r = await axios.post(
        `${API}/b2b/orders/${order.id}/send-cotation`,
        payload,
        { headers: auth() },
      );
      if (r.data?.errors?.length) {
        // Affiche les erreurs non bloquantes (ex : numéro absent côté backend)
        r.data.errors.forEach((e) => toast.warning(e));
      }
      if (withPhone && r.data?.whatsapp_url) {
        window.open(r.data.whatsapp_url, '_blank', 'noopener');
        toast.success('WhatsApp ouvert · envoi tracé dans l\'historique');
      } else {
        // Mode "sans numéro" : on copie le message + ouvre wa.me vide
        try {
          await navigator.clipboard.writeText(previewMsg);
          toast.success('Message copié · WhatsApp ouvert · envoi tracé');
        } catch (_) {
          toast.success('WhatsApp ouvert · envoi tracé (copie clipboard refusée par le navigateur)');
        }
        window.open(`https://wa.me/?text=${encodeURIComponent(previewMsg)}`, '_blank', 'noopener');
      }
      onChanged?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erreur lors de l\'envoi');
    } finally {
      setSimpleSendBusy(false);
    }
  };

  const copyMessage = async () => {
    if (!previewMsg) return;
    try {
      await navigator.clipboard.writeText(previewMsg);
      toast.success('Message copié dans le presse-papier');
    } catch (_) {
      toast.info('Sélectionnez et copiez manuellement le message');
    }
  };

  // ─── Option 2 · Envoi pour signature électronique ──────────────────────
  const sendForValidation = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API}/b2b/orders/${order.id}/cotation/send-for-validation`, {}, { headers: auth() });
      toast.success('Lien de validation cotation généré');
      onChanged?.();
      // Auto-copy on first generation
      const url = `${window.location.origin}${r.data.public_path}`;
      try { await navigator.clipboard.writeText(url); toast.info('Lien copié dans le presse-papier'); } catch (_) { /* ignore */ }
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erreur');
    } finally { setSending(false); }
  };

  const copyValidationLink = () => {
    if (!publicUrl) return;
    navigator.clipboard.writeText(publicUrl).then(
      () => toast.success('Lien copié'),
      () => toast.info(publicUrl),
    );
  };

  const shareValidationWhatsApp = () => {
    if (!publicUrl) return;
    const msg = `Bonjour ${order.clinic_name || ''},\n\nVoici votre cotation à valider :\n${publicUrl}\n\nPharmacie Saint Ange Des Amis`;
    const url = `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(url, '_blank');
  };

  return (
    <div className="space-y-3" data-testid="b2b-pdf-workflow">
      <h3 className="text-sm font-semibold text-zinc-800 flex items-center gap-1.5">
        <Sparkles className="w-4 h-4 text-violet-500" /> Workflow PDF Médiciel
      </h3>

      {!hasPdf && (
        <p className="text-xs text-zinc-500 italic p-3 bg-zinc-50 rounded-lg">
          Importez d'abord le PDF de cotation (section ci-dessus) pour activer les options d'envoi.
        </p>
      )}

      {hasPdf && (
        <div className="space-y-3">
          <p className="text-xs text-zinc-500">
            Choisissez comment transmettre la cotation à la clinique :
          </p>

          {/* ═══ OPTION 1 · Envoi simple WhatsApp (raw PDF) ═══════════════ */}
          <div
            className="border-2 border-emerald-200 bg-emerald-50/40 rounded-lg p-3"
            data-testid="b2b-option-simple"
          >
            <div className="flex items-start gap-2 mb-2">
              <div className="shrink-0 w-7 h-7 rounded-full bg-emerald-600 text-white text-xs font-bold flex items-center justify-center">1</div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-emerald-900 flex items-center gap-1.5">
                  <MessageCircle className="w-4 h-4" /> Envoyer la cotation PDF (simple)
                </p>
                <p className="text-[11px] text-emerald-800/80 mt-0.5">
                  Envoi rapide via WhatsApp avec un lien direct vers le PDF brut. Pas de signature électronique. Action tracée dans l'historique.
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-1.5 mt-2">
              {canWrite && (
                <>
                  <Button
                    size="sm"
                    onClick={() => sendRawPdfWhatsApp({ withPhone: true })}
                    disabled={simpleSendBusy || !clinicPhone}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1"
                    data-testid="b2b-simple-send-clinic"
                    title={!clinicPhone ? 'Aucun téléphone sur le dossier' : `Envoyer à ${clinicPhone}`}
                  >
                    {simpleSendBusy
                      ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      : <Send className="w-3.5 h-3.5 mr-1.5" />}
                    Envoyer à la clinique
                    {clinicPhone && (
                      <span className="ml-1 text-[10px] opacity-90 hidden sm:inline">({clinicPhone})</span>
                    )}
                  </Button>

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => sendRawPdfWhatsApp({ withPhone: false })}
                    disabled={simpleSendBusy}
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-100 flex-1"
                    data-testid="b2b-simple-send-manual"
                  >
                    <Share2 className="w-3.5 h-3.5 mr-1.5" />
                    Copier &amp; ouvrir WhatsApp
                  </Button>
                </>
              )}
            </div>

            <div className="mt-2 flex items-center gap-2 text-[11px]">
              <button
                type="button"
                onClick={() => setShowPreview((v) => !v)}
                className="text-emerald-700 hover:text-emerald-900 underline underline-offset-2"
                data-testid="b2b-simple-toggle-preview"
              >
                {showPreview ? 'Masquer' : 'Aperçu'} du message
              </button>
              {previewMsg && (
                <button
                  type="button"
                  onClick={copyMessage}
                  className="text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"
                  data-testid="b2b-simple-copy-msg"
                >
                  <Copy className="w-3 h-3" /> Copier le texte
                </button>
              )}
              {!clinicPhone && (
                <span className="inline-flex items-center gap-1 text-amber-700 ml-auto">
                  <Phone className="w-3 h-3" /> Numéro absent
                </span>
              )}
            </div>

            {showPreview && previewMsg && (
              <pre
                className="mt-2 p-2 bg-white border border-emerald-200 rounded text-[11px] text-zinc-700 whitespace-pre-wrap leading-snug"
                data-testid="b2b-simple-preview"
              >
                {previewMsg}
              </pre>
            )}
          </div>

          {/* ═══ OPTION 2 · Extraction + signature électronique ═══════════ */}
          <div
            className="border-2 border-violet-200 bg-violet-50/40 rounded-lg p-3"
            data-testid="b2b-option-esign"
          >
            <div className="flex items-start gap-2 mb-2">
              <div className="shrink-0 w-7 h-7 rounded-full bg-violet-600 text-white text-xs font-bold flex items-center justify-center">2</div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-violet-900 flex items-center gap-1.5">
                  <PenLine className="w-4 h-4" /> Extraire &amp; Signer électroniquement
                </p>
                <p className="text-[11px] text-violet-800/80 mt-0.5">
                  Parse les lignes du PDF puis envoie un lien sécurisé pour signature en ligne par la clinique.
                </p>
              </div>
            </div>

            {canWrite && (
              <Button
                onClick={() => setExtractOpen(true)}
                variant="outline"
                size="sm"
                className="w-full border-violet-300 text-violet-700 hover:bg-violet-50 mb-2"
                data-testid="extract-pdf-btn"
              >
                <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                Étape A · Extraire les lignes du PDF
              </Button>
            )}

            {/* Bloc validation cotation */}
            <div className="border border-violet-200 bg-white rounded-lg p-2.5">
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-xs font-medium text-zinc-800">Étape B · Validation par la clinique</p>
                {isValidated && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-semibold">
                    <CheckCircle2 className="w-3 h-3" /> Signée
                  </span>
                )}
                {isModRequested && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold" data-testid="cotation-badge-modification">
                    <PenLine className="w-3 h-3" /> Modification demandée
                  </span>
                )}
                {isRejected && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-800 text-[10px] font-semibold" data-testid="cotation-badge-rejected">
                    <X className="w-3 h-3" /> Rejetée
                  </span>
                )}
              </div>

              {isValidated ? (
                <div className="text-xs space-y-1 text-emerald-800">
                  <p>✍️ Signée par <strong>{order.cotation_validated_by_name}</strong>
                    {order.cotation_validated_by_role && ` (${order.cotation_validated_by_role})`}</p>
                  <p className="text-zinc-500">
                    Le {new Date(order.cotation_validated_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                  </p>
                  {order.cotation_signature_png_base64 && (
                    <img src={order.cotation_signature_png_base64} alt="Signature" className="mt-2 max-w-[200px] bg-white border border-emerald-200 rounded" />
                  )}
                </div>
              ) : isModRequested && modRequest ? (
                <div className="text-xs space-y-1.5 text-amber-900" data-testid="cotation-modrequest-block">
                  <p>
                    ✏️ Par <strong>{modRequest.by_name}</strong>
                    {modRequest.by_role && ` (${modRequest.by_role})`}
                  </p>
                  <p className="text-zinc-500">
                    Le {new Date(modRequest.at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                  </p>
                  <div className="bg-amber-50 border border-amber-200 rounded p-2 text-zinc-800 whitespace-pre-wrap text-[11px]">
                    {modRequest.comment}
                  </div>
                  {modRequest.requested_items?.length > 0 && (
                    <div>
                      <p className="font-semibold text-amber-900 mt-1.5">Lignes souhaitées ({modRequest.requested_items.length}) :</p>
                      <ul className="list-disc list-inside text-[11px] space-y-0.5 text-zinc-700">
                        {modRequest.requested_items.map((it, i) => (
                          <li key={i}>
                            <strong>{it.designation}</strong>
                            {it.quantity != null && ` · qté ${it.quantity}`}
                            {it.observation && ` — ${it.observation}`}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <p className="text-[10px] text-zinc-500 italic mt-2">
                    👉 Re-téléversez une nouvelle cotation PDF (section ci-dessus) pour répondre à la demande,
                    ou changez le statut du dossier en « Annulée » si vous renoncez.
                  </p>
                </div>
              ) : isRejected && rejection ? (
                <div className="text-xs space-y-1.5 text-red-900" data-testid="cotation-rejection-block">
                  <p>
                    ❌ Par <strong>{rejection.by_name}</strong>
                    {rejection.by_role && ` (${rejection.by_role})`}
                  </p>
                  <p className="text-zinc-500">
                    Le {new Date(rejection.at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                  </p>
                  <div className="bg-red-50 border border-red-200 rounded p-2 text-zinc-800 whitespace-pre-wrap text-[11px]">
                    <span className="font-semibold">Motif : </span>{rejection.comment}
                  </div>
                  <p className="text-[10px] text-zinc-500 italic mt-2">
                    👉 Vous pouvez préparer une nouvelle cotation ou annuler le dossier.
                  </p>
                </div>
              ) : hasValidationToken ? (
                <div className="space-y-1.5">
                  <p className="text-[11px] text-zinc-600">Lien public envoyé · en attente de signature</p>
                  <div className="flex gap-1.5 flex-wrap">
                    <Button size="sm" variant="outline" onClick={copyValidationLink} data-testid="cotation-copy-link">
                      <Copy className="w-3 h-3 mr-1" /> Copier le lien
                    </Button>
                    <Button size="sm" variant="outline" onClick={shareValidationWhatsApp} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="cotation-whatsapp">
                      <MessageCircle className="w-3 h-3 mr-1" /> WhatsApp
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  onClick={sendForValidation}
                  disabled={sending || !canWrite}
                  size="sm"
                  className="bg-violet-600 hover:bg-violet-700 text-white"
                  data-testid="cotation-send-validation"
                >
                  {sending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                  Générer le lien de signature
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {extractOpen && (
        <ExtractModal
          orderId={order.id}
          onClose={() => setExtractOpen(false)}
          onApplied={onChanged}
        />
      )}
    </div>
  );
};

export default B2BPdfWorkflowSection;
